import { Auth0Client, createAuth0Client, User } from "@auth0/auth0-spa-js";
import { saveSession } from "@platform/user/client";

import envVariables from "../projectEnvVariables";

let auth0Client: Auth0Client | null = null;

const IMPERSONATING_BROKER_ID = "impersonating_broker_id";
const IMPERSONATING_BROKER_ROLE = "impersonating_broker_role";
const IMPERSONATED_BROKER_KEY = "impersonatedBrokerId";
const CENTRO_WORKPLACE_CONNECTION = "CentroWorkplace";

const ALLOWED_REDIRECT_PARAMS = [
  IMPERSONATING_BROKER_ID,
  IMPERSONATING_BROKER_ROLE,
];

/**
 * Build redirect URI with only allowed query params preserved
 */
const callbackUri = (): string => `${window.location.origin}/centro/callback`;

const buildRedirectUri = (): string => {
  const baseUri = callbackUri();
  const searchParams = new URLSearchParams(window.location.search);
  const allowedParams = new URLSearchParams();

  ALLOWED_REDIRECT_PARAMS.forEach((param) => {
    if (searchParams.has(param)) {
      allowedParams.set(param, searchParams.get(param)!);
    }
  });

  const queryString = allowedParams.toString();
  return queryString ? `${baseUri}?${queryString}` : baseUri;
};

interface AppState {
  returnTo?: string;
}

interface CallbackResult {
  success: boolean;
  user?: User;
  token?: string;
  appState?: AppState | null;
  error?: string;
}

/**
 * Initialize the Auth0 client with PKCE configuration
 */
async function getAuth0Client(connection?: string): Promise<Auth0Client> {
  if (!auth0Client) {
    auth0Client = await createAuth0Client({
      domain: envVariables.VITE_REACT_APP_AUTH0_DOMAIN as string,
      clientId: envVariables.VITE_REACT_APP_AUTH0_CLIENT_ID as string,
      authorizationParams: {
        redirect_uri: buildRedirectUri(),
        audience: envVariables.VITE_REACT_APP_AUTH0_CLIENT_ID as string,
        ...(connection && { connection }),
      },
      cacheLocation: "localstorage",
      useRefreshTokens: true,
    });
  }
  return auth0Client;
}

/**
 * Initiate Auth0 login with PKCE flow
 */
export async function loginWithAuth0(
  connection: string,
  redirectUri: string = buildRedirectUri(),
): Promise<void> {
  // Store the connection being used for authentication
  localStorage.setItem("auth0Connection", connection);
  const client = await getAuth0Client(connection);
  await client.loginWithRedirect({
    authorizationParams: {
      connection,
      redirect_uri: redirectUri,
    },
  });
}

/**
 * Get the connection currently being authenticated with Auth0
 */
export function getAuth0Connection(): string | null {
  return localStorage.getItem("auth0Connection");
}

/**
 * Clear the stored Auth0 connection
 */
export function clearAuth0Connection(): void {
  localStorage.removeItem("auth0Connection");
}

let pendingCallback: {
  searchString: string;
  result: Promise<CallbackResult>;
} | null = null;

/**
 * Handle the redirect callback after Auth0 authentication
 * @param callbackSearch - The URL search string (e.g. from React Router's location.search)
 *                         containing the `code` and `state` params from Auth0.
 */
export function handleAuth0Callback(
  callbackSearch?: string,
): Promise<CallbackResult> {
  // Use the search string passed from the caller (React Router preserves it
  // in location.search even when the browser URL has already been rewritten).
  // Fall back to window.location.search as a last resort.
  const searchString = callbackSearch || window.location.search;
  // The SDK accepts each authorization code once, so a callback page that
  // remounts shares the first call's result instead of failing on its own.
  if (pendingCallback?.searchString !== searchString) {
    pendingCallback = {
      searchString,
      result: completeAuth0Callback(searchString),
    };
  }
  return pendingCallback.result;
}

async function completeAuth0Callback(
  searchString: string,
): Promise<CallbackResult> {
  const client = await getAuth0Client();

  try {
    // Check if we have the necessary query parameters
    const searchParams = new window.URLSearchParams(searchString);
    const hasCode = searchParams.has("code");
    const hasState = searchParams.has("state");

    if (!hasCode || !hasState) {
      // No callback parameters found, check if already authenticated
      const isAuthenticated = await client.isAuthenticated();
      if (isAuthenticated) {
        const user = await client.getUser();
        const token = await client.getTokenSilently();
        return {
          success: true,
          user,
          token,
          appState: null,
        };
      }
      return {
        success: false,
        error: "No authentication callback parameters found",
      };
    }

    // Build the full callback URL with the preserved params
    const currentUrl = window.location.href.split("?")[0];
    const callbackUrl = `${currentUrl}${searchString}`;

    // Pass the URL directly to handleRedirectCallback to avoid race conditions
    // with history.replaceState and React Router listeners
    const result = await client.handleRedirectCallback(callbackUrl);

    // Get user information and tokens
    const user = await client.getUser();
    const token = await client.getTokenSilently();
    const idToken = await client.getIdTokenClaims();

    // The SDK keeps its refresh token in its own cache, so SSO sessions always stay signed in.
    saveSession(
      { accessToken: token, idToken: idToken?.__raw },
      { keepSignedIn: true, provider: "sso" },
    );
    // Clear the connection so the app renders Dashboard instead of CentroLogin
    localStorage.removeItem("auth0Connection");
    // The redirect_uri carries the impersonation params back, so every
    // callback tells us whether this session is impersonating a broker.
    const impersonatedBrokerId = searchParams.get(IMPERSONATING_BROKER_ID);
    if (impersonatedBrokerId) {
      localStorage.setItem(IMPERSONATED_BROKER_KEY, impersonatedBrokerId);
    } else {
      clearBrokerImpersonation();
    }

    return {
      success: true,
      user,
      token,
      appState: result.appState as AppState | undefined,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error handling Auth0 callback:", error);

    // "Invalid state" typically means the PKCE transaction was overwritten
    // (e.g., user double-clicked login or had a stale tab). Check if the
    // user ended up authenticated despite the state mismatch.
    const isInvalidState =
      error instanceof Error && /invalid state/i.test(error.message);
    if (isInvalidState) {
      try {
        const isAuthenticated = await client.isAuthenticated();
        if (isAuthenticated) {
          const user = await client.getUser();
          const token = await client.getTokenSilently();
          clearAuth0Connection();
          return {
            success: true,
            user,
            token,
            appState: null,
          };
        }
      } catch {
        // Recovery failed — fall through to error return
      }
    }

    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}

export function isImpersonatingBroker(): boolean {
  return localStorage.getItem(IMPERSONATED_BROKER_KEY) !== null;
}

export function clearBrokerImpersonation(): void {
  localStorage.removeItem(IMPERSONATED_BROKER_KEY);
}

/**
 * Sign in again through the bare callback, which returns the user to their own
 * account. The impersonation mark stays until that callback clears it, so a
 * failed or abandoned exit can be retried.
 */
export async function exitBrokerImpersonation(): Promise<void> {
  try {
    await loginWithAuth0(CENTRO_WORKPLACE_CONNECTION, callbackUri());
  } catch (error: unknown) {
    // A leftover connection would send the next page load into the login flow
    clearAuth0Connection();
    throw error;
  }
}
